import algoliasearch from 'algoliasearch/lite';
import React, { useRef } from 'react';
import {
  Configure,
  HierarchicalMenu,
  Hits,
  HitsPerPage,
  InstantSearch,
  Pagination,
  RefinementList,
  SearchBox,
  SortBy,
  ToggleRefinement,
  Highlight,
  Snippet,
} from 'react-instantsearch';

import {
  AlgoliaSvg,
  ClearFilters,
  ClearFiltersMobile,
  NoResults,
  NoResultsBoundary,
  Panel,
  PriceSlider,
  Ratings,
  ResultsNumberMobile,
  SaveFiltersMobile,
} from './components';
import { ScrollTo } from './components/ScrollTo';
import getRouting from './routing';
import { formatNumber } from './utils';
import './Theme.css';
import './App.css';
import './components/Pagination.css';
import './App.mobile.css';

import type { Hit as AlgoliaHit } from 'instantsearch.js';

const searchClient = algoliasearch(
  "LFA75E93U5",
  "4386543dd94c928dae7b34f9ffc7573b",
  // stalledSearchDelay: 200, // default
);

let timerId: any = undefined;
let timeout: any = 250;
function queryHook(query: any, search: any) {
  if (timerId) {
    clearTimeout(timerId);
  }
  timerId = setTimeout(() => search(query), timeout);
}

// Get language from URL parameter (using sortBy for routing compatibility)
function getLanguageFromURL(): 'fr' | 'en' | 'he' {
  const params = new URLSearchParams(window.location.search);
  const sortBy = params.get('sortBy') || 'PITAETCHOCOLAT_FR';

  // Map index name to language
  if (sortBy === 'PITAETCHOCOLAT_EN') return 'en';
  if (sortBy === 'PITAETCHOCOLAT_HE') return 'he';
  return 'fr';
}

// Map language to Algolia index
function getIndexName(lang: 'fr' | 'en' | 'he'): string {
  const indexMap = {
    fr: 'PITAETCHOCOLAT_FR',
    en: 'PITAETCHOCOLAT_EN',
    he: 'PITAETCHOCOLAT_HE',
  };
  return indexMap[lang];
}

// Also support legacy 'lang' parameter for backwards compatibility
function getLegacyLanguageParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang');
  if (lang === 'en') return 'PITAETCHOCOLAT_EN';
  if (lang === 'he') return 'PITAETCHOCOLAT_HE';
  if (lang === 'fr') return 'PITAETCHOCOLAT_FR';
  return null;
}

// Get translations for UI text
function getTranslations(lang: 'fr' | 'en' | 'he') {
  const translations = {
    fr: {
      tagline: "Cuisine juive d'aujourd'hui.",
      searchPlaceholder: "Recette, ingrédient, description, tag, cuisine…",
      filters: "Filtres",
      tags: "Tags",
      categories: "Catégories",
      searchTag: "Recherche par tag",
      searchCategory: "Recherche par catégorie",
      loading: "Chargement…",
      perPage: (n: number) => `${n} par page`,
    },
    en: {
      tagline: "Modern Jewish cuisine.",
      searchPlaceholder: "Recipe, ingredient, description, tag, cuisine…",
      filters: "Filters",
      tags: "Tags",
      categories: "Categories",
      searchTag: "Search by tag",
      searchCategory: "Search by category",
      loading: "Loading…",
      perPage: (n: number) => `${n} per page`,
    },
    he: {
      tagline: "מטבח יהודי עכשווי.",
      searchPlaceholder: "מתכון, רכיב, תיאור, תג, מטבח…",
      filters: "מסננים",
      tags: "תגיות",
      categories: "קטגוריות",
      searchTag: "חיפוש לפי תג",
      searchCategory: "חיפוש לפי קטגוריה",
      loading: "טוען…",
      perPage: (n: number) => `${n} לעמוד`,
    },
  };
  return translations[lang];
}

// Support both ?sortBy=PITAETCHOCOLAT_EN and legacy ?lang=en
const legacyIndex = getLegacyLanguageParam();
if (legacyIndex && !window.location.search.includes('sortBy')) {
  // Redirect from ?lang=X to ?sortBy=PITAETCHOCOLAT_X
  const newUrl = new URL(window.location.href);
  newUrl.searchParams.delete('lang');
  newUrl.searchParams.set('sortBy', legacyIndex);
  window.history.replaceState({}, '', newUrl.toString());
}

const currentLang = getLanguageFromURL();
const indexName = getIndexName(currentLang);
const routing = getRouting(indexName);
const t = getTranslations(currentLang);

export function App() {
  const containerRef = useRef<HTMLElement>(null);
  const headerRef = useRef(null);

  function openFilters() {
    document.body.classList.add('filtering');
    window.scrollTo(0, 0);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('click', onClick);
  }

  function closeFilters() {
    document.body.classList.remove('filtering');
    containerRef.current!.scrollIntoView();
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('click', onClick);
  }

  function onKeyUp(event: { key: string }) {
    if (event.key !== 'Escape') {
      return;
    }

    closeFilters();
  }

  function onClick(event: MouseEvent) {
    if (event.target !== headerRef.current) {
      return;
    }

    closeFilters();
  }

  return (
    <div dir={currentLang === 'he' ? 'rtl' : 'ltr'}>
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      routing={routing}
      insights={true}
    >
      <header className="header" ref={headerRef}>
        <p className="header-logo">
          <a href="https://pitaetchocolat.com/">
            <img
              src="https://pitaetchocolat.com/images/pita-et-chocolat-logo-white.png"
              alt="logo pita & chocolat"
              width={450}
            />
          </a>
        </p>

        <p className="header-title">{t.tagline}</p>

        <SearchBox
          placeholder={t.searchPlaceholder}
          submitIconComponent={SubmitIcon}
          queryHook={queryHook}
          loadingIconComponent={() => t.loading}
        />
      </header>

      <Configure
        attributesToSnippet={['description:10']}
        snippetEllipsisText="…"
        removeWordsIfNoResults="allOptional"
      />

      <ScrollTo>
        <main className="container" ref={containerRef}>
          <div className="container-wrapper">
            <section className="container-filters" onKeyUp={onKeyUp}>
              <div className="container-header">
                <h2>{t.filters}</h2>

                <div className="clear-filters" data-layout="desktop">
                  <ClearFilters />
                </div>

                <div className="clear-filters" data-layout="mobile">
                  <ResultsNumberMobile />
                </div>
              </div>

              <div className="container-body">
                {false && <>
                <Panel header="Category">
                  <HierarchicalMenu
                    attributes={[
                      'hierarchicalCategories.lvl0',
                      'hierarchicalCategories.lvl1',
                    ]}
                  />
                </Panel>

                <Panel header="Brands">
                  <RefinementList
                    attribute="brand"
                    searchable={true}
                    searchablePlaceholder="Search for brands…"
                  />
                </Panel>

                <Panel header="Price">
                  <PriceSlider attribute="price" />
                </Panel>

                <Panel header="Free shipping">
                  <ToggleRefinement
                    attribute="free_shipping"
                    label="Display only items with free shipping"
                    on={true}
                  />
                </Panel>

                <Panel header="Ratings">
                  <Ratings attribute="rating" />
                </Panel>
                </>}

                <Panel header={t.tags}>
                  <RefinementList
                    attribute="tags"
                    operator="and"
                    showMore
                    searchable={true}
                    searchablePlaceholder={t.searchTag}
                    translations={{
                      // FIXME
                      // noResults: 'Rien qui convienne !',
                      // showMore(expanded) {
                      //   return expanded ? 'Voir moins' : 'Voir plus';
                      // },
                    }}
                  />
                </Panel>

                <Panel header={t.categories}>
                  <RefinementList
                    attribute="categories"
                    operator="or"
                    searchable={true}
                    searchablePlaceholder={t.searchCategory}
                  />
                </Panel>
              </div>
            </section>

            <footer className="container-filters-footer" data-layout="mobile">
              <div className="container-filters-footer-button-wrapper">
                <ClearFiltersMobile containerRef={containerRef} />
              </div>

              <div className="container-filters-footer-button-wrapper">
                <SaveFiltersMobile onClick={closeFilters} />
              </div>
            </footer>
          </div>

          <section className="container-results">
            <header className="container-header container-options">
              {false && <SortBy
                className="container-option"
                items={[
                  {
                    label: 'Français',
                    value: 'PITAETCHOCOLAT_FR',
                  },
                  {
                    label: 'Hébreu',
                    value: 'PITAETCHOCOLAT_HE',
                  },
                  {
                    label: 'Anglais',
                    value: 'PITAETCHOCOLAT_EN',
                  },
                ]}
                defaultValue='PITAETCHOCOLAT_FR'
              />}

              <HitsPerPage
                className="container-option"
                items={[
                  {
                    label: t.perPage(20),
                    value: 20,
                    default: true,
                  },
                  {
                    label: t.perPage(40),
                    value: 40,
                  },
                  {
                    label: t.perPage(60),
                    value: 60,
                  },
                ]}
                defaultValue={20}
              />
            </header>

            <NoResultsBoundary fallback={<NoResults />}>
              <Hits hitComponent={Hit} />
            </NoResultsBoundary>

            <footer className="container-footer">
              <Pagination padding={2} showFirst={false} showLast={false} />
            </footer>
          </section>
        </main>
      </ScrollTo>

      <aside data-layout="mobile">
        <button
          className="filters-button"
          data-action="open-overlay"
          onClick={openFilters}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14">
            <path
              d="M15 1H1l5.6 6.3v4.37L9.4 13V7.3z"
              stroke="#fff"
              strokeWidth="1.29"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t.filters}
        </button>
      </aside>
    </InstantSearch>
    </div>
  );
}

function SubmitIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 18 18"
      aria-hidden="true"
    >
      <g
        fill="none"
        fillRule="evenodd"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.67"
        transform="translate(1 1)"
      >
        <circle cx="7.11" cy="7.11" r="7.11" />
        <path d="M16 16l-3.87-3.87" />
      </g>
    </svg>
  );
}

type HitType = AlgoliaHit<{
  permalink: string;
  images: string[];
  categories: string[];
  tags: string[];
  ingredients: string[];
  title: string;
  description: string;
  price: number;
  rating: string;
}>;

function Hit({ hit }: { hit: HitType }) {
  return (
    <article className="hit">
      <header className="hit-image-container">
        <a href={hit.permalink}>
        <img src={"https://pitaetchocolat.com/"+hit.images[0]} alt={hit.title} className="hit-image" />
        </a>
      </header>

      <div className="hit-info-container">
        <p className="hit-category">{hit.categories[0]}</p>
        <h1>
          <a href={hit.permalink}>
          <Highlight attribute="title" highlightedTagName="mark" hit={hit} />
          </a>
        </h1>
        <p className="hit-description">
          <Snippet
            attribute="description"
            highlightedTagName="mark"
            hit={hit}
          />
        </p>

        {false && <footer>
          <p>
            <span className="hit-em">$</span>{' '}
            <strong>{formatNumber(hit.price)}</strong>{' '}
            <span className="hit-em hit-rating">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="8"
                viewBox="0 0 16 16"
              >
                <path
                  fill="#e2a400"
                  fillRule="evenodd"
                  d="M10.472 5.008L16 5.816l-4 3.896.944 5.504L8 12.616l-4.944 2.6L4 9.712 0 5.816l5.528-.808L8 0z"
                />
              </svg>{' '}
              {hit.rating}
            </span>
          </p>
        </footer>}
      </div>
    </article>
  );
}
